import { chiSvg } from "./chi_sprite";

export type AvatarMood = "idle" | "thinking" | "happy";

const HAPPY_MS = 3500;
let happyTimer = 0;

export function setMood(mood: AvatarMood): void {
  window.clearTimeout(happyTimer);
  document.querySelectorAll<HTMLElement>(".chix-avatar").forEach((avatar) => {
    avatar.classList.toggle("thinking", mood === "thinking");
    avatar.classList.toggle("happy", mood === "happy");
  });
  if (mood === "happy") happyTimer = window.setTimeout(() => setMood("idle"), HAPPY_MS);
}

// the bigger slots get a psx pass: her render redrawn at a low resolution with a 15 bit palette and an
// ordered dither, then scaled up with hard pixels. drawn once per slot, the picture is cached after the first
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
let psxFrame: Promise<HTMLCanvasElement | null> | null = null;

function psxChi(source: HTMLImageElement): Promise<HTMLCanvasElement | null> {
  psxFrame ??= new Promise((resolve) => {
    const draw = () => {
      const size = 88;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ink = canvas.getContext("2d", { willReadFrequently: true });
      if (!ink || !source.naturalWidth) return resolve(null);
      // cover the square, framed on her face like the css object-position
      const side = Math.min(source.naturalWidth, source.naturalHeight);
      const sx = (source.naturalWidth - side) / 2;
      const sy = (source.naturalHeight - side) * 0.3;
      ink.imageSmoothingEnabled = true;
      ink.drawImage(source, sx, sy, side, side, 0, 0, size, size);
      const image = ink.getImageData(0, 0, size, size);
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const at = (y * size + x) * 4;
          const bias = (BAYER[(y % 4) * 4 + (x % 4)] / 16 - 0.5) * 8;
          for (let channel = 0; channel < 3; channel++) {
            const value = Math.max(0, Math.min(255, image.data[at + channel] + bias));
            image.data[at + channel] = Math.round(value / 8) * 8;
          }
        }
      }
      ink.putImageData(image, 0, 0);
      resolve(canvas);
    };
    if (source.complete && source.naturalWidth) draw();
    else {
      source.addEventListener("load", draw, { once: true });
      source.addEventListener("error", () => resolve(null), { once: true });
    }
  });
  return psxFrame;
}

function psxSlot(slot: HTMLElement): void {
  const image = slot.querySelector<HTMLImageElement>("img.chi");
  if (!image) return;
  void psxChi(image).then((frame) => {
    if (!frame || !image.isConnected) return;
    const copy = document.createElement("canvas");
    copy.width = frame.width;
    copy.height = frame.height;
    copy.getContext("2d")?.drawImage(frame, 0, 0);
    copy.className = "chi chi-psx";
    copy.setAttribute("aria-hidden", "true");
    image.replaceWith(copy);
  });
}

export function stampAvatars(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>(".chix-avatar").forEach((slot) => {
    if (slot.childElementCount) return;
    slot.innerHTML = chiSvg();
    if (slot.closest(".viewfinder")) psxSlot(slot);
  });
}

export function setupAvatars(): void {
  stampAvatars();
  document.addEventListener("click", (event) => {
    const avatar = (event.target as Element).closest?.(".chix-avatar:not(.tiny)") as HTMLElement | null;
    if (!avatar) return;
    avatar.classList.remove("cheer");
    void avatar.offsetWidth;
    avatar.classList.add("cheer");
    setMood("happy");
  });
}
